/**
 * Domain Variation Generator for Cold Email Infrastructure
 * 
 * Generates realistic, brand-safe .com domain variations based on client domain
 * Following rules for prefix/suffix combinations for optimal deliverability
 */

export interface DomainGenerationOptions {
  clientDomain: string
  maxDomains?: number
  includePrefixes?: boolean
  includeTier3?: boolean
}

export interface GeneratedDomain {
  domain: string
  tier: 1 | 2 | 3
  type: 'prefix' | 'suffix'
  available?: boolean
  price?: number
}

/**
 * Extract root domain from URL or domain string
 * Example: "cruva.com" -> "cruva", "https://www.cruva.com" -> "cruva"
 */
export function extractRootDomain(domain: string): string {
  if (!domain) return ''
  
  // Remove protocol
  domain = domain.replace(/^https?:\/\//, '')
  // Remove www
  domain = domain.replace(/^www\./, '')
  // Remove path, query, fragment
  domain = domain.split('/')[0].split('?')[0].split('#')[0]
  // Remove TLD
  domain = domain.split('.').slice(0, -1).join('.') || domain.split('.')[0]
  
  return domain.toLowerCase().trim()
}

/**
 * Tier 1 suffixes (60% of domains) - Primary, high credibility
 * Ordered by priority (best deliverability first)
 */
const TIER_1_SUFFIXES = [
  'hq',        // Highest credibility - headquarters
  'team',      // High credibility - team
  'work',      // Professional - work
  'co',        // Company abbreviation
  'pro',       // Professional
  'group',     // Business group
  'mail',      // Email/mail (good for deliverability)
  'dev'        // Development
]

/**
 * Tier 2 suffixes (30% of domains) - Secondary, good credibility
 * Ordered by priority
 */
const TIER_2_SUFFIXES = [
  'works',     // Professional work
  'labs',      // Innovation/R&D
  'tech',      // Technology
  'inc',       // Incorporated
  'partners',  // Partnership
  'base',      // Base/headquarters
  'flow',      // Workflow
  'studio',    // Creative studio
  'post',      // Post/communication
  'world'      // Global reach
]

/**
 * Tier 3 suffixes (10% of domains) - Filler, backup
 */
const TIER_3_SUFFIXES = [
  'care', 'connect', 'support', 'craft', 'build', 'brand', 'made', 'link', 'suite', 'portal'
]

/**
 * Allowed prefixes (≤20% of domains)
 * Ordered by priority (most natural/brandable first)
 */
const PREFIXES = [
  'try',   // Most natural (trycruva.com)
  'get',   // Action-oriented (getcruva.com)
  'go',    // Simple action (gocruva.com)
  'join',  // Community-oriented
  'use',   // Utility-focused
  'meet',  // Meeting/contact
  'my',    // Personal
  'with',  // Partnership
  'hey'    // Casual/friendly
]

/**
 * Calculate optimal domain length limit based on root domain length
 * Automatically adjusts to ensure enough variations can be generated
 */
export function calculateOptimalDomainLength(rootDomain: string): number {
  const rootLength = rootDomain.length
  
  // Short domains (1-5 chars): Default limit is fine
  // Examples: "cruva" (5), "loop" (4)
  if (rootLength <= 5) {
    return 13 // Default, plenty of room for suffixes/prefixes
  }
  
  // Medium domains (6-8 chars): Need slightly more room
  // Examples: "outbound" (8), "smartlead" (9)
  if (rootLength <= 8) {
    return 16 // Allows 8-10 char suffixes/prefixes
  }
  
  // Long domains (9-11 chars): Need more room
  // Examples: "storystream" (11)
  if (rootLength <= 11) {
    return 20 // Allows 9-10 char suffixes/prefixes
  }
  
  // Very long domains (12+ chars): Maximum flexibility
  // Examples: "outboundbuddy" (13)
  return 25 // Allows 12-13 char suffixes/prefixes
}

/**
 * Filter domain variations
 */
function isValidDomain(domain: string, isSuffix: boolean = false, maxLength: number = 13): boolean {
  // Must be <= maxLength chars before .com (auto-calculated based on root domain)
  if (domain.length > maxLength) return false
  
  // Must read cleanly
  if (!/^[a-z0-9]+$/.test(domain)) return false
  
  // Avoid obvious spam patterns (but allow legitimate suffixes like 'mail' in tier lists)
  // Only block if it's a prefix or standalone, not if it's a legitimate suffix
  if (!isSuffix) {
    const spamPatterns = ['spam', 'email', 'send', 'marketing', 'sales', 'deal', 'offer']
    // Allow 'mail' as it's a valid Tier 1 suffix
    if (spamPatterns.some(pattern => domain.includes(pattern))) return false
  }
  
  return true
}

/**
 * Generate domain variations based on rules
 */
export function generateDomainVariations(options: DomainGenerationOptions): GeneratedDomain[] {
  const { clientDomain, maxDomains = 200, includePrefixes = true, includeTier3 = true } = options
  
  const root = extractRootDomain(clientDomain)
  if (!root) return []
  
  // Automatically calculate optimal domain length limit based on root domain
  const maxDomainLength = calculateOptimalDomainLength(root)
  
  const generated: GeneratedDomain[] = []
  
  // Calculate distribution
  const prefixPercentage = includePrefixes ? 0.2 : 0 // 20% prefixes
  const tier1Percentage = 0.6 // 60% tier 1 suffixes
  const tier2Percentage = 0.3 // 30% tier 2 suffixes
  const tier3Percentage = includeTier3 ? 0.1 : 0 // 10% tier 3 suffixes
  
  const prefixCount = Math.floor(maxDomains * prefixPercentage)
  const tier1Count = Math.floor(maxDomains * tier1Percentage)
  const tier2Count = Math.floor(maxDomains * tier2Percentage)
  const tier3Count = includeTier3 ? Math.floor(maxDomains * tier3Percentage) : 0
  
  // Generate prefix domains (e.g., getcruva.com, trycruva.com)
  if (includePrefixes && prefixCount > 0) {
    for (const prefix of PREFIXES) {
      if (generated.length >= prefixCount) break
      
      const domain = `${prefix}${root}`
      if (isValidDomain(domain, false, maxDomainLength)) {
        generated.push({
          domain: `${domain}.com`,
          tier: 1,
          type: 'prefix'
        })
      }
    }
  }
  
  // Generate Tier 1 suffix domains (e.g., cruvahq.com, cruvateam.com)
  for (const suffix of TIER_1_SUFFIXES) {
    if (generated.filter(d => d.type === 'suffix' && d.tier === 1).length >= tier1Count) break
    
    const domain = `${root}${suffix}`
    if (isValidDomain(domain, true, maxDomainLength)) {
      generated.push({
        domain: `${domain}.com`,
        tier: 1,
        type: 'suffix'
      })
    }
  }
  
  // Generate Tier 2 suffix domains (e.g., cruvaworks.com, cruvalabs.com)
  for (const suffix of TIER_2_SUFFIXES) {
    if (generated.filter(d => d.type === 'suffix' && d.tier === 2).length >= tier2Count) break
    
    const domain = `${root}${suffix}`
    if (isValidDomain(domain, true, maxDomainLength)) {
      generated.push({
        domain: `${domain}.com`,
        tier: 2,
        type: 'suffix'
      })
    }
  }
  
  // Generate Tier 3 suffix domains (backup)
  if (includeTier3 && tier3Count > 0) {
    for (const suffix of TIER_3_SUFFIXES) {
      if (generated.filter(d => d.type === 'suffix' && d.tier === 3).length >= tier3Count) break
      
      const domain = `${root}${suffix}`
      if (isValidDomain(domain, true, maxDomainLength)) {
        generated.push({
          domain: `${domain}.com`,
          tier: 3,
          type: 'suffix'
        })
      }
    }
  }
  
  // Generate additional dynamic variations if we haven't reached maxDomains
  if (generated.length < maxDomains) {
    const additionalSuffixes = [
      'app', 'online', 'live', 'site', 'web', 'cloud', 'space', 'plus', 
      'now', 'next', 'x', 'new', 'go', 
      'io', 'tech', 'digital', 'platform', 'net', 'solutions', 'systems',
      'tools', 'services', 'agency', 'media', 'global', 'group', 'ventures'
    ]
    
    for (const suffix of additionalSuffixes) {
      if (generated.length >= maxDomains) break
      const domain = `${root}${suffix}`
      if (isValidDomain(domain, true, maxDomainLength)) {
        const fullDomain = `${domain}.com`
        if (!generated.find(d => d.domain === fullDomain)) {
          generated.push({
            domain: fullDomain,
            tier: 3,
            type: 'suffix'
          })
        }
      }
    }
  }
  
  // Maintain generation order (already prioritized by tier and suffix/prefix order)
  // Don't sort - keep the priority order as generated
  // Domains are generated in priority order:
  // 1. Prefixes (Tier 1) - ordered by priority
  // 2. Tier 1 suffixes - ordered by priority (hq, team, work, co, pro, group, mail, dev)
  // 3. Tier 2 suffixes - ordered by priority (works, labs, tech, inc, partners, base, flow, studio, post, world)
  // 4. Tier 3 suffixes - ordered by priority
  // 5. Additional dynamic suffixes
  
  return generated
}

/**
 * Generate domains for a specific count (e.g., 41 domains)
 * Prioritizes Tier 1 and Tier 2, fills with Tier 3 if needed
 * Ensures we generate exactly the requested count, even if some domains fail validation
 */
export function generateDomainsForCount(
  clientDomain: string,
  count: number,
  includeTier3: boolean = true
): GeneratedDomain[] {
  const root = extractRootDomain(clientDomain)
  if (!root) return []
  
  // Generate enough variations to ensure we have valid domains
  const allVariations = generateDomainVariations({
    clientDomain,
    maxDomains: count * 2, // Generate 2x needed for selection variety
    includePrefixes: true,
    includeTier3
  })
  
  // Prioritize by tier and order:
  // 1. Tier 1 suffixes (best deliverability)
  // 2. Tier 2 suffixes (good deliverability)
  // 3. Tier 1 prefixes (limited to ≤20%)
  // 4. Tier 3 suffixes (backup)
  
  const tier1Suffixes = allVariations.filter(d => d.tier === 1 && d.type === 'suffix')
  const tier2Suffixes = allVariations.filter(d => d.tier === 2 && d.type === 'suffix')
  const tier1Prefixes = allVariations.filter(d => d.tier === 1 && d.type === 'prefix')
  const tier3Suffixes = includeTier3 ? allVariations.filter(d => d.tier === 3 && d.type === 'suffix') : []
  
  const selected: GeneratedDomain[] = []
  const maxPrefixes = Math.floor(count * 0.2) // Limit prefixes to 20%
  
  // 1. Add Tier 1 suffixes (highest priority)
  selected.push(...tier1Suffixes.slice(0, count))
  
  // 2. Fill with Tier 2 suffixes if needed
  if (selected.length < count) {
    const remaining = count - selected.length
    selected.push(...tier2Suffixes.slice(0, remaining))
  }
  
  // 3. Add Tier 1 prefixes (up to 20% limit)
  if (selected.length < count) {
    const prefixSlots = Math.min(maxPrefixes, count - selected.length)
    selected.push(...tier1Prefixes.slice(0, prefixSlots))
  }
  
  // 4. Fill remaining with Tier 3 suffixes if needed
  if (selected.length < count && includeTier3) {
    const remaining = count - selected.length
    selected.push(...tier3Suffixes.slice(0, remaining))
  }
  
  // 5. If still not enough, generate additional Tier 3 domains on the fly
  if (selected.length < count && includeTier3) {
    const remaining = count - selected.length
    // Automatically calculate optimal domain length limit
    const maxDomainLength = calculateOptimalDomainLength(root)
    // Generate more Tier 3 domains by trying additional professional suffixes
    const additionalSuffixes = ['app', 'online', 'live', 'site', 'web', 'cloud', 'space', 'plus', 'now', 'next', 'x', 'new']
    for (const suffix of additionalSuffixes) {
      if (selected.length >= count) break
      const domain = `${root}${suffix}`
      if (isValidDomain(domain, true, maxDomainLength)) {
        const fullDomain = `${domain}.com`
        // Avoid duplicates
        if (!selected.find(d => d.domain === fullDomain)) {
          selected.push({
            domain: fullDomain,
            tier: 3,
            type: 'suffix'
          })
        }
      }
    }
  }
  
  // 6. If still not enough, add more prefixes beyond 20% limit
  if (selected.length < count) {
    const remaining = count - selected.length
    selected.push(...tier1Prefixes.slice(maxPrefixes, maxPrefixes + remaining))
  }
  
  return selected.slice(0, count)
}

/**
 * Get tier label for display
 */
export function getTierLabel(tier: 1 | 2 | 3): string {
  switch (tier) {
    case 1:
      return 'Tier 1 - Primary'
    case 2:
      return 'Tier 2 - Secondary'
    case 3:
      return 'Tier 3 - Backup'
    default:
      return 'Unknown'
  }
}

/**
 * Get tier description
 */
export function getTierDescription(tier: 1 | 2 | 3): string {
  switch (tier) {
    case 1:
      return 'High credibility, best for warm-up and primary sending'
    case 2:
      return 'Good credibility, suitable for secondary sending'
    case 3:
      return 'Backup domains, reserve for automations or fallback'
    default:
      return ''
  }
}

